import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft, Loader2, CheckCircle2, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { delivery: { sms_sent, email_sent, channels_attempted } }

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    setSubmitting(true);
    try {
      const res = await authAPI.passwordResetRequest(trimmed);
      setResult(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send reset link');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#060913] text-white flex items-center justify-center px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-6">
          <img src="/logo-main.png" alt="Elev802" className="h-20 w-auto mx-auto rounded-2xl" />
          <h1 className="font-['Barlow_Condensed'] text-3xl font-black tracking-tight mt-4">FORGOT PASSWORD</h1>
          <p className="text-xs text-[#94A3B8] mt-2">We'll text or email you a one-time reset link.</p>
        </div>

        <div className="bg-[#0F172A] border border-[#1C2B54] rounded-2xl p-6">
          {!result ? (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-form">
              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#94A3B8]">Account email</span>
                <div className="relative mt-1">
                  <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    autoFocus
                    required
                    className="w-full bg-[#060913] border border-[#1C2B54] rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#2563EB] transition-colors"
                    data-testid="forgot-email-input"
                  />
                </div>
              </label>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-[#2563EB] to-[#00E5FF] text-[#060913] font-black uppercase tracking-wide rounded-lg px-6 py-3 hover:opacity-90 transition-opacity disabled:opacity-40 flex items-center justify-center gap-2"
                data-testid="forgot-submit-btn"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : 'Send Reset Link'}
              </button>
            </form>
          ) : (
            <div className="text-center py-2" data-testid="forgot-result">
              <CheckCircle2 size={32} className="mx-auto text-[#10B981] mb-3" />
              <p className="font-['Barlow_Condensed'] text-lg font-bold mb-1">Check your phone &amp; inbox</p>
              <p className="text-xs text-[#CBD5E1] leading-relaxed mb-4">
                If an account exists for <span className="font-semibold text-white">{email}</span>, we just sent a reset link.
              </p>

              {/* Delivery summary */}
              {result.delivery && (
                <div className="bg-[#060913] border border-[#1C2B54] rounded-lg px-4 py-3 mb-4 text-left">
                  <div className="flex items-center gap-2 text-xs">
                    <MessageSquare size={12} className={result.delivery.sms_sent ? 'text-[#10B981]' : 'text-[#475569]'} />
                    <span className={result.delivery.sms_sent ? 'text-[#10B981] font-bold' : 'text-[#64748B]'}>
                      SMS {result.delivery.sms_sent ? 'sent' : 'not sent'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs mt-1.5">
                    <Mail size={12} className={result.delivery.email_sent ? 'text-[#10B981]' : 'text-[#475569]'} />
                    <span className={result.delivery.email_sent ? 'text-[#10B981] font-bold' : 'text-[#64748B]'}>
                      Email {result.delivery.email_sent ? 'sent' : 'not sent'}
                    </span>
                  </div>
                  {(!result.delivery.sms_sent && !result.delivery.email_sent) && (
                    <p className="text-[10px] text-[#94A3B8] mt-2 leading-snug">
                      If you don't receive a message within a minute, contact a coach for a manual reset link.
                    </p>
                  )}
                </div>
              )}

              <button
                onClick={() => navigate('/login')}
                className="text-[#00E5FF] text-xs font-bold uppercase tracking-widest underline-offset-2 hover:underline"
                data-testid="back-to-signin-btn"
              >
                Back to Sign In
              </button>
            </div>
          )}
        </div>

        <Link
          to="/login"
          className="flex items-center justify-center gap-1.5 text-[10px] text-[#64748B] uppercase tracking-widest mt-4 hover:text-white transition-colors"
        >
          <ArrowLeft size={11} /> Back to Sign In
        </Link>
      </motion.div>
    </div>
  );
}
